import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import ScoreComponent from './ScoreComponent';
import CriteriaComponent from './CriteriaComponent';
import ProgressBarComp from './ProgressBarComponent';
import SemanticComponent from './SemanticComponent';
import StatsComponent from './StatsComponent';
import InfoComponent from './InfoComponent';
import { exportAnalysisReportToExcel } from '../utils/report';

const sanitizeFilename = (value) => {
    if (!value || typeof value !== 'string') {
        return 'analyse-seo';
    }
    const withoutProtocol = value.replace(/^https?:\/\//i, '');
    const slug = withoutProtocol
        .replace(/[^a-z0-9]+/gi, '-')
        .replace(/-{2,}/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase();
    return slug || 'analyse-seo';
};

const DisplayComponent = ({ data, isLoading, url, keyword }) => {
    const [criteriaConfig, setCriteriaConfig] = useState(null);

    useEffect(() => {
        const fetchCriteria = async () => {
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/criteria`);
                const json = await resp.json();
                setCriteriaConfig(json);
            } catch (e) {
                // En cas d'erreur, laisser les titres/descriptions vides
                setCriteriaConfig({ criteria: [] });
            }
        };
        fetchCriteria();
    }, []);

    const { mandatoryTitles, optionalTitles, bonusTitles, mandatoryDescriptions, optionalDescriptions, bonusDescriptions, mandatoryCount, optionalCount, bonusCount } = useMemo(() => {
        const mTitles = {};
        const oTitles = {};
        const bTitles = {};
        const mDesc = {};
        const oDesc = {};
        const bDesc = {};
        let mCount = 0;
        let oCount = 0;
        let bCount = 0;
        (criteriaConfig?.criteria || []).forEach(c => {
            if (c.group === 'mandatory') {
                mTitles[c.key] = c.label;
                if (c.description) mDesc[c.key] = c.description;
                mCount += 1;
            } else if (c.group === 'optional') {
                oTitles[c.key] = c.label;
                if (c.description) oDesc[c.key] = c.description;
                oCount += 1;
            } else if (c.group === 'bonus') {
                bTitles[c.key] = c.label;
                if (c.description) bDesc[c.key] = c.description;
                bCount += 1;
            }
        });
        return {
            mandatoryTitles: mTitles,
            optionalTitles: oTitles,
            bonusTitles: bTitles,
            mandatoryDescriptions: mDesc,
            optionalDescriptions: oDesc,
            bonusDescriptions: bDesc,
            mandatoryCount: mCount,
            optionalCount: oCount,
            bonusCount: bCount
        };
    }, [criteriaConfig]);

    const mandatoryData = data?.mandatory || {};
    const optionalData = data?.optional || {};
    const bonusData = data?.bonus || {};

    const criteriaMeta = useMemo(() => ({
        mandatory: {
            titles: mandatoryTitles,
            descriptions: mandatoryDescriptions,
            total: mandatoryCount || (mandatoryData ? Object.keys(mandatoryData).length : undefined),
        },
        optional: {
            titles: optionalTitles,
            descriptions: optionalDescriptions,
            total: optionalCount || (optionalData ? Object.keys(optionalData).length : undefined),
        },
        bonus: {
            titles: bonusTitles,
            descriptions: bonusDescriptions,
            total: bonusCount || (bonusData ? Object.keys(bonusData).length : undefined),
        },
    }), [mandatoryTitles, optionalTitles, bonusTitles, mandatoryDescriptions, optionalDescriptions, bonusDescriptions, mandatoryCount, optionalCount, bonusCount, mandatoryData, optionalData, bonusData]);

    const handleDownloadReport = useCallback(() => {
        if (!data) {
            return;
        }
        const filename = `rapport-seo-${sanitizeFilename(url || keyword)}.xlsx`;
        exportAnalysisReportToExcel({ data, url, keyword, criteriaMeta, filename });
    }, [data, url, keyword, criteriaMeta]);

    if (isLoading) {
        return (
            <div className="alert alert-primary" id="loading">
                <div className="d-flex align-items-center">
                    <div className="spinner-border me-2" role="status" aria-hidden="true"></div>
                    <span>Analyse des <strong>critères SEO</strong>, de la <strong>sémantique</strong> et des <strong>statistiques</strong> de votre page...</span>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="alert alert-primary" id="loading">Entrez votre adresse et votre requête clé pour lancer l'analyse.</div>;
    }

    const score = data.score ?? 0;
    const scoreMandatory = data.scoreMandatory ?? 0;
    const scoreOptional = data.scoreOptional ?? 0;
    const scoreBonus = data.scoreBonus ?? 0;
    const nbrsmot = data.nbrsmot ?? 0;
    const locationDetails = data.locationDetails || {};

    return (
        <div>
            <div className="alert alert-warning py-2">
                    <strong>Points de vigilance :</strong>
                    <ul className="mb-0">
                        <li className="list-group-item"><i className="bi bi-images me-2"></i> Vérifiez que vos images sont bien libres de droit</li>
                        <li className="list-group-item"><i className="bi bi-ui-checks me-2"></i> Vérifiez que vos formulaires respectent les régles du RGPD</li>
                    </ul>
            </div>

            <div className="d-flex justify-content-end mb-3">
                <Button
                    type="button"
                    variant="primary"
                    onClick={handleDownloadReport}
                    disabled={!data}
                    className="d-inline-flex align-items-center"
                >
                    <i className="bi bi-download me-2"></i>
                    Télécharger le compte rendu
                </Button>
            </div>

            <div className="card bg-dark mb-4">
                <div className="card-header text-white h5">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <span className="d-inline-flex align-items-center gap-2">
                            <i className="bi bi-check2-circle"></i>
                            Critères de notation à observer afin d'améliorer votre score SEO
                        </span>
                        <InfoComponent
                            text="Passez en revue les critères obligatoires, complémentaires et bonus : chaque badge détaille l’action SEO à appliquer pour augmenter votre score."
                            style={{ marginLeft: 0, marginBottom: 0 }}
                        />
                    </div>
                </div>

                <div className="card-body text-white p-2 align-items-center-">

                    <ScoreComponent
                        scores={{score, scoreMandatory, scoreOptional, scoreBonus}}
                        mandatoryCount={mandatoryCount || 5}
                        optionalCount={optionalCount || 12}
                    />

                    <CriteriaComponent
                        mandatory={mandatoryData}
                        optional={optionalData}
                        bonus={bonusData}
                        mandatoryDescriptions={mandatoryDescriptions}
                        optionalDescriptions={optionalDescriptions}
                        bonusDescriptions={bonusDescriptions}
                        mandatoryTitles={mandatoryTitles}
                        optionalTitles={optionalTitles}
                        bonusTitles={bonusTitles}
                        locationDetails={locationDetails}
                    />

                    <ProgressBarComp scoreNbrsMot={nbrsmot} />

                </div>

            </div>

            <div className="card bg-dark mb-4">
              <div className="card-header text-white h5 d-flex align-items-center">
                <i className="bi bi-magic me-2"></i>
                Analyse sémantique
                <InfoComponent
                  text="Évalue automatiquement la cohérence, la lisibilité, la richesse du vocabulaire, la longueur des phrases, l'usage du passif et la couverture des thématiques UniCA pour révéler les axes d'amélioration."
                  style={{
                    marginLeft: "0.5rem", marginBottom: 0
                  }}
                />
              </div>
              <div className="card-body p-2 text-white">

                <SemanticComponent data={data}/>

              </div>
            </div>


            <div className="card bg-dark">
              <div className="card-header text-white h5">
                <i className="bi bi-clipboard2-data-fill small"></i> Indicateurs de visites (Matomo)
              </div>
              <div className="card-body p-2 text-white">

                <StatsComponent data={data} />

              </div>
            </div>
        </div>
    );
};

export default DisplayComponent;
